import { useEffect, useState } from 'react'
import { auth } from '../firebase'
import { updatePassword } from 'firebase/auth'
import { subscribeParent, subscribeParentChildren, subscribeIsAdmin } from '../lib/db'
import { effectiveStatus, statusLabel } from '../lib/billing'
import { formatPrice, PLAN_PRICES, getStoredCurrency, deviceLimitFor } from '../lib/currency'
import { PAYMENT_CONFIG, whatsappContactUrl } from '../lib/payments'
import { toast } from '../lib/toast'
import PaymentOptions from '../components/PaymentOptions'
import type { Parent, Child } from '../lib/types'
import type { Tab } from '../lib/nav'

export default function AccountPage({
  onBack,
  onOpenAdmin,
  onOpenDownloads,
  onOpenChild,
}: {
  onBack: () => void
  onOpenAdmin: () => void
  onOpenDownloads: () => void
  onOpenChild: (childUid: string, childTab?: Tab) => void
}) {
  const [parent, setParent] = useState<Parent | null>(null)
  const [devices, setDevices] = useState<{ uid: string; data: Child }[]>([])
  const [isAdminUser, setIsAdminUser] = useState(false)
  const [curPw, setCurPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [pwMsg, setPwMsg] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const uid = auth.currentUser?.uid ?? ''

  useEffect(() => {
    if (!uid) return
    const un1 = subscribeParent(uid, setParent)
    const un2 = subscribeParentChildren(uid, setDevices)
    const un3 = subscribeIsAdmin(uid, setIsAdminUser)
    return () => { un1(); un2(); un3() }
  }, [uid])

  // El SuperAdmin/dueño no tiene planes ni vencimientos: todo lo de
  // suscripción se oculta y el estado siempre es activo.
  const status = effectiveStatus(parent?.billing, isAdminUser)

  const handleChangePassword = async () => {
    setPwBusy(true)
    try {
      const user = auth.currentUser
      if (user) {
        await updatePassword(user, newPw)
      }
      setPwMsg('')
      setCurPw('')
      setNewPw('')
      toast('Contrasena actualizada', 'success')
    } catch (e: unknown) {
      const err = e as { code?: string }
      if (err.code === 'auth/weak-password') setPwMsg('La contrasena debe tener al menos 6 caracteres')
      else if (err.code === 'auth/requires-recent-login') setPwMsg('Inicia sesion de nuevo para cambiar la contrasena')
      else setPwMsg('No se pudo cambiar la contrasena')
    } finally {
      setPwBusy(false)
    }
  }

  const firstChildUid = devices[0]?.uid

  return (
    <div className="flex min-h-full bg-slate-50">
      <aside className="hidden w-64 shrink-0 flex-col bg-brand-800 text-white md:flex">
        <div className="flex items-center gap-3 px-5 py-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-xl">
            🛡️
          </div>
          <div>
            <p className="font-bold leading-tight">KidSafe</p>
            <p className="text-xs text-brand-200">Control Parental</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          <button
            onClick={onBack}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
          >
            <span>🏠</span> Inicio
          </button>
          <button
            onClick={() => firstChildUid && onOpenChild(firstChildUid)}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
          >
            <span>📊</span> Actividad
          </button>
          <button
            onClick={() => firstChildUid && onOpenChild(firstChildUid, 'apps')}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
          >
            <span>📱</span> Aplicaciones
          </button>
          <button
            onClick={onOpenDownloads}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-brand-100 transition hover:bg-white/5"
          >
            <span>📥</span> Descargas
          </button>
          {isAdminUser && onOpenAdmin && (
            <button
              onClick={onOpenAdmin}
              className="mt-2 flex w-full items-center gap-3 rounded-lg border border-white/15 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/10"
            >
              <span>🛡️</span> SuperAdmin
            </button>
          )}
        </nav>
        <div className="border-t border-white/10 p-4">
          {parent?.name && (
            <p className="mb-2 truncate text-sm font-medium text-white">{parent.name}</p>
          )}
          <button
            onClick={() => auth.signOut()}
            className="w-full rounded-lg border border-white/20 px-4 py-2 text-sm text-white transition hover:bg-white/10"
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              ← Volver
            </button>
            <h1 className="text-lg font-bold text-slate-800">Mi cuenta</h1>
          </div>
          <div className="flex items-center gap-3">
            {parent?.name && (
              <span className="hidden text-sm text-slate-500 sm:block">
                Hola, <b className="text-slate-700">{parent.name}</b>
              </span>
            )}
            <button
              onClick={() => auth.signOut()}
              className="md:hidden rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600"
            >
              Salir
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 pb-20 md:p-6 md:pb-6">
          <div className="mx-auto max-w-3xl space-y-5">
            <div className="flex items-center gap-4 rounded-2xl bg-gradient-to-r from-brand-600 to-brand-700 p-6 text-white shadow-md">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 text-3xl">
                👤
              </div>
              <div>
                <p className="font-bold leading-tight">{parent?.name ?? 'Usuario'}</p>
                <p className="text-xs text-brand-100">{parent?.email}</p>
              </div>
            </div>

            {isAdminUser && (
              <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
                <p className="font-bold text-brand-800">🛡️ Cuenta de SuperAdmin</p>
                <p className="mt-1 text-sm text-brand-700">
                  Como dueño de la app no tienes planes, pagos ni vencimientos.
                  La suscripción solo aplica a los clientes registrados.
                </p>
              </div>
            )}

            {!isAdminUser && parent?.billing && (
              <>
                <div className="grid gap-5 sm:grid-cols-3">
                  <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Plan</p>
                    <p className="mt-2 text-2xl font-bold text-slate-800">
                      {parent.billing.plan === 'monthly'
                        ? `Mensual · ${parent.billing.planTier === 'premium' ? 'Premium' : 'Familia'}`
                        : 'Prueba gratuita'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Dispositivos</p>
                    <p className="mt-2 text-2xl font-bold text-slate-800">
                      {devices.length}
                      <span className="text-sm font-medium text-slate-400"> / {deviceLimitFor(parent?.billing)}</span>
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Estado</p>
                    {parent.billing?.plan === 'monthly' && parent.billing?.activeUntil
                      ? <p className="mt-2 text-2xl font-bold text-slate-800">
                          {new Date(parent.billing.activeUntil).toLocaleDateString('es')}
                        </p>
                      : parent.billing?.trialEnd
                        ? <p className="mt-2 text-2xl font-bold text-slate-800">
                            {new Date(parent.billing.trialEnd).toLocaleDateString('es')}
                          </p>
                        : <p className="mt-2 text-2xl font-bold text-slate-800">—</p>
                    }
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-800">Suscripcion</h3>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                      status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      status === 'trial' ? 'bg-sky-100 text-sky-700' :
                      status === 'expired' ? 'bg-rose-100 text-rose-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {statusLabel(status).text}
                    </span>
                  </div>

                  {parent.billing?.plan === 'monthly' && parent.billing && (
                    <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-2">Suscripcion</p>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-700">Plan</p>
                        <b className="text-slate-800 capitalize">
                          {parent.billing.planTier === 'premium' ? 'Premium' : 'Familia'}
                        </b>
                      </div>
                      <div className="mt-1 flex justify-between py-2 text-sm">
                        <span className="text-slate-500">Precio</span>
                        <span className="font-semibold text-slate-800">
                          {formatPrice(
                            parent.billing.planTier === 'premium'
                              ? PLAN_PRICES.premium[getStoredCurrency()]
                              : PLAN_PRICES.familia[getStoredCurrency()],
                            getStoredCurrency()
                          )}/mes
                        </span>
                      </div>
                      {parent.billing.activeUntil && (
                        <div className="flex justify-between py-2 text-sm">
                          <span className="text-slate-500">Valido hasta</span>
                          <span className="font-semibold text-slate-800">
                            {new Date(parent.billing.activeUntil).toLocaleDateString('es')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {!isAdminUser && status !== 'active' && (
                    <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
                      <h3 className="font-bold text-slate-800">
                        {status === 'expired' ? 'Reactiva tu suscripcion' : 'Mejora tu plan'}
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Elige un plan, paga y el administrador lo activara en minutos.
                      </p>
                      <div className="mt-4">
                        <PaymentOptions
                          uid={uid}
                          name={parent?.name ?? ''}
                          email={parent?.email ?? ''}
                          currentTier={parent?.billing?.planTier}
                        />
                      </div>
                    </div>
                  )}

                  {/* Limite de dispositivos alcanzado */}
                  {!isAdminUser && status === 'active' && devices.length >= deviceLimitFor(parent?.billing) && (
                    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-bold text-amber-800">
                            Limite del plan alcanzado ({devices.length} dispositivos)
                          </h3>
                          <p className="mt-1 text-sm text-amber-700">
                            {parent?.billing?.planTier === 'familia'
                              ? 'Estas en el plan mas alto. Gracias por confiar en KidSafe!'
                              : 'Mejora tu plan para vincular mas dispositivos y proteger a toda tu familia.'}
                          </p>
                        </div>
                      </div>
                      {parent?.billing?.planTier !== 'familia' && (
                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                          <button
                            onClick={() => {
                              if (PAYMENT_CONFIG.wompiCheckoutUrl.includes('TU_LINK')) {
                                toast('El enlace de pago aun no esta configurado.', 'error')
                                return
                              }
                              window.open(PAYMENT_CONFIG.wompiCheckoutUrl, '_blank')
                            }}
                            className="flex items-center justify-center gap-2 rounded-xl bg-[#2D2E83] py-3 font-semibold text-white transition hover:bg-sky-700">
                            Pagar con Wompi
                          </button>
                          <a
                            href={whatsappContactUrl(
                              `Hola, soy ${parent?.name || parent?.email} y quiero mejorar mi plan de KidSafe a Familia.`
                            )}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
                          >
                            Contactar por WhatsApp
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
              <h3 className="font-bold text-slate-800">Cambiar contrasena</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <input
                  type="password"
                  value={curPw}
                  onChange={(e) => setCurPw(e.target.value)}
                  placeholder="Contrasena actual"
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-500"
                />
                <input
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder="Nueva contrasena"
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-500"
                />
              </div>
              {pwMsg && <p className="mt-2 text-sm text-rose-600">{pwMsg}</p>}
              <button
                onClick={handleChangePassword}
                disabled={pwBusy || !curPw || !newPw}
                className="mt-3 w-full rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {pwBusy ? 'Guardando...' : 'Cambiar contrasena'}
              </button>
            </div>

            <p className="text-center text-xs text-slate-400">
              KidSafe Control parental Protege a tus hijos en todos sus dispositivos
            </p>
          </div>
        </main>
      </div>
    </div>
  )
}